import React from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { McpMergedCapability } from '../../../../types/aiTypes';

type Props = {
  capabilities: McpMergedCapability[];
  selectedCapabilityKey?: string | null;
  onSelectCapability?: (capability: McpMergedCapability) => void;
  onEvaluateCapability?: (capability: McpMergedCapability) => void;
  onPromoteDiscovered?: (capability: McpMergedCapability) => void;
  promoting?: boolean;
};

const boolChip = (value: any) => (
  <Chip
    size="small"
    color={value ? 'success' : 'default'}
    variant={value ? 'filled' : 'outlined'}
    label={String(Boolean(value))}
  />
);

const MCPCapabilitiesTable: React.FC<Props> = ({
                                                 capabilities,
                                                 selectedCapabilityKey,
                                                 onSelectCapability,
                                                 onEvaluateCapability,
                                                 onPromoteDiscovered,
                                                 promoting,
                                               }) => {
  const [filter, setFilter] = React.useState('');

  const rows = React.useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return capabilities;

    return capabilities.filter((cap) => {
      return [
        cap.display_name,
        cap.capability_name,
        cap.name,
        cap.description,
        cap.capability_kind,
        cap.capability_type,
        cap.uri_template,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [capabilities, filter]);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box mb={2}>
        <TextField
          fullWidth
          size="small"
          placeholder="Filter capabilities by name, kind, description, or URI..."
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            minWidth: 1180,
            '& td, & th': {
              verticalAlign: 'top',
              wordBreak: 'break-word',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 290 }}>Name</TableCell>
              <TableCell sx={{ width: 110 }}>Kind</TableCell>
              <TableCell sx={{ width: 95 }}>Configured</TableCell>
              <TableCell sx={{ width: 95 }}>Discovered</TableCell>
              <TableCell sx={{ width: 85 }}>Allowed</TableCell>
              <TableCell sx={{ width: 85 }}>Enabled</TableCell>
              <TableCell sx={{ width: 250 }}>URI Template</TableCell>
              <TableCell sx={{ width: 170 }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((cap) => {
              const selected = selectedCapabilityKey === cap.key;
              const canPromote = !cap.configured && cap.discovered;

              return (
                <TableRow
                  key={cap.key}
                  hover
                  selected={selected}
                  onClick={() => onSelectCapability?.(cap)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      {cap.display_name || cap.capability_name || cap.name}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', whiteSpace: 'normal' }}
                    >
                      {cap.description || '-'}
                    </Typography>
                  </TableCell>

                  <TableCell>{cap.capability_kind || cap.capability_type || '-'}</TableCell>
                  <TableCell>{boolChip(cap.configured)}</TableCell>
                  <TableCell>{boolChip(cap.discovered)}</TableCell>
                  <TableCell>{boolChip(cap.allowed)}</TableCell>
                  <TableCell>{boolChip(cap.enabled)}</TableCell>

                  <TableCell>
                    <Typography variant="caption">
                      {cap.uri_template || '-'}
                    </Typography>
                  </TableCell>

                  <TableCell onClick={(event) => event.stopPropagation()}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {canPromote && onPromoteDiscovered ? (
                        <Tooltip title="Create a configured capability from this discovered capability">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!!promoting}
                              onClick={() => onPromoteDiscovered(cap)}
                            >
                              Add
                            </Button>
                          </span>
                        </Tooltip>
                      ) : null}

                      {onEvaluateCapability ? (
                        <Tooltip title="Open dynamic evaluation dialog">
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<PlayArrowIcon fontSize="small" />}
                              disabled={!cap.enabled}
                              onClick={() => onEvaluateCapability(cap)}
                            >
                              Test
                            </Button>
                          </span>
                        </Tooltip>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {!rows.length ? (
        <Typography variant="body2" color="text.secondary" mt={2}>
          No capabilities found.
        </Typography>
      ) : null}
    </Paper>
  );
};

export default MCPCapabilitiesTable;